import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { PageTopButton } from '../atoms';
import HeaderTitle from "../atoms/HeaderTitle";
import LoginFilter from "../atoms/LoginFilter";
import { getSleepRecords } from "../store/actions";

const SleepCalendar = props => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadRecords = async (year, month) => {
    setLoading(true);
    try {
      const response = await dispatch(getSleepRecords(year, month));
      if (response && response.records) {
        setRecords(response.records);
      }
    } catch (err) {
      console.error('Failed to load sleep records:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadRecords(currentYear, currentMonth);
  }, [currentYear, currentMonth]);

  const prevMonth = () => {
    if (currentMonth === 1) {
      setCurrentYear(currentYear - 1);
      setCurrentMonth(12);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 12) {
      setCurrentYear(currentYear + 1);
      setCurrentMonth(1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const getStatusColor = (status, withAlpha = false) => {
    // withAlpha=trueの場合は透明度30%で返す
    switch (status) {
      case 'complete':
        return withAlpha ? 'rgba(76, 175, 80, 0.3)' : '#4CAF50'; // 緑
      case 'partial':
        return withAlpha ? 'rgba(255, 193, 7, 0.3)' : '#FFC107'; // 黄色
      case 'empty':
      default:
        return withAlpha ? 'rgba(244, 67, 54, 0.3)' : '#F44336'; // 赤
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'complete':
        return '入力済';
      case 'partial':
        return '一部入力';
      case 'empty':
      default:
        return '未入力';
    }
  };

  const handleDateClick = (date) => {
    navigate(`/top/${date}`);
  };

  // 今日の日付を取得
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  // カレンダーの日付を生成
  const generateCalendarDays = () => {
    const firstDay = new Date(currentYear, currentMonth - 1, 1);
    const lastDay = new Date(currentYear, currentMonth, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();

    const days = [];

    // 前月の空白
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push({ date: null, status: null });
    }

    // 当月の日付
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const record = records.find(r => r.date === dateStr);
      const isFuture = dateStr > todayStr;
      days.push({
        date: dateStr,
        day: day,
        status: record ? record.status : 'empty',
        bedtime: record ? record.bedtime : null,
        wakeup_time: record ? record.wakeup_time : null,
        isToday: dateStr === todayStr,
        isFuture: isFuture,
      });
    }

    return days;
  };

  const calendarDays = generateCalendarDays();
  const weekDays = ['日', '月', '火', '水', '木', '金', '土'];

  return (
    <div className="l-wrap">
      <LoginFilter />
      <HeaderTitle />

      <div style={{
        maxWidth: '600px',
        margin: '0 auto',
        padding: '60px 0 30px',
        backgroundColor: '#eff9ff',
        minHeight: '100vh'
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '15px 0' }}>
          <button
            onClick={prevMonth}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 'calc(24px * var(--font-scale, 1))',
              cursor: 'pointer',
              padding: '0 20px'
            }}
          >
            ◀
          </button>
          <span style={{ fontSize: 'calc(18px * var(--font-scale, 1))', fontWeight: 'bold' }}>
            {currentYear}年{currentMonth}月
          </span>
          <button
            onClick={nextMonth}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 'calc(24px * var(--font-scale, 1))',
              cursor: 'pointer',
              padding: '0 20px'
            }}
          >
            ▶
          </button>
        </div>

        <div style={{ padding: '0 15px' }}>
          {/* カレンダーヘッダー */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '2px',
            marginBottom: '5px'
          }}>
            {weekDays.map((day, index) => (
              <div
                key={day}
                style={{
                  textAlign: 'center',
                  padding: '8px 0',
                  fontWeight: 'bold',
                  color: index === 0 ? '#F44336' : index === 6 ? '#2196F3' : '#333'
                }}
              >
                {day}
              </div>
            ))}
          </div>

          {/* カレンダー本体 */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>読み込み中...</div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '2px'
            }}>
              {calendarDays.map((item, index) => (
                <div
                  key={index}
                  onClick={() => item.date && handleDateClick(item.date)}
                  style={{
                    minHeight: '60px',
                    padding: '5px',
                    backgroundColor: item.date ? (item.isFuture ? '#f9f9f9' : getStatusColor(item.status, true)) : 'transparent',
                    border: item.date ? (item.isToday ? '2px solid #2196F3' : '1px solid #e0e0e0') : 'none',
                    borderRadius: '4px',
                    cursor: item.date && !item.isFuture ? 'pointer' : 'default',
                    position: 'relative'
                  }}
                >
                  {item.date && (
                    <div style={{
                      fontSize: 'calc(14px * var(--font-scale, 1))',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      color: item.isToday ? '#2196F3' : 'inherit'
                    }}>
                      {item.day}
                      {item.isToday && <span style={{ fontSize: 'calc(10px * var(--font-scale, 1))', display: 'block', color: '#2196F3' }}>今日</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* 凡例 - カレンダーの下に配置 */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '10px',
            marginTop: '15px',
            fontSize: 'calc(11px * var(--font-scale, 1))'
          }}>
            <span><span style={{ display: 'inline-block', width: '10px', height: '10px', backgroundColor: '#4CAF50', borderRadius: '50%', marginRight: '4px' }}></span>前日就寝・当日起床入力済み</span>
            <span><span style={{ display: 'inline-block', width: '10px', height: '10px', backgroundColor: '#FFC107', borderRadius: '50%', marginRight: '4px' }}></span>どちらか一方が未入力</span>
            <span><span style={{ display: 'inline-block', width: '10px', height: '10px', backgroundColor: '#F44336', borderRadius: '50%', marginRight: '4px' }}></span>両方未入力</span>
          </div>

          {/* 戻るボタン - 凡例の30px下に配置 */}
          <div style={{
            marginTop: '30px',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <button
              className="c-btn c-btn-line c-btn-xl"
              onClick={() => navigate("/top")}
              style={{ width: '100%' }}
            >
              戻る
            </button>
          </div>
        </div>
      </div>

      <PageTopButton />
    </div>
  );
};

export default SleepCalendar;
